import { styled } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  useScrollTrigger,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ThemeToggle } from '../../../components/ThemeToggle';
import { getGlassBackground } from '../../../theme/helpers';
import { useAuth } from '../../../contexts/AuthContext';
import { useLogout } from '../../../hooks/auth/useLogout';
import { AvatarDropdown } from '../../../components/navigation/AvatarDropdown';
import { CitySwitcher } from '../../../components/CitySwitcher';
import { Logo } from '../../../components/shared';

interface LandingNavbarProps {
  transparent?: boolean;
}

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})<{ scrolled?: boolean }>(({ theme, scrolled }) => ({
  background: scrolled ? getGlassBackground(theme) : 'transparent',
  backdropFilter: scrolled ? 'blur(10px)' : 'none',
  borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : 'none',
  boxShadow: scrolled ? '0 2px 12px rgba(0, 0, 0, 0.05)' : 'none',
  transition: 'all 0.3s ease-in-out',
}));

const LogoLink = styled(RouterLink)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textDecoration: 'none',
  '&:hover': {
    '& .logo-icon': {
      transform: 'rotate(-10deg) scale(1.1)',
    },
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'none',
  gap: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const NavLink = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  fontSize: '0.9375rem',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.7,
  },
}));

const AuthButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const NAV_ITEMS = [
  { label: 'Browse Venues', href: '/venues', isRouter: true },
  { label: 'How It Works', href: '/#how-it-works', isRouter: false },
  { label: 'List Your Venue', href: '/list-your-venue', isRouter: true },
];

export function LandingNavbar({ transparent = true }: LandingNavbarProps) {
  const { isAuthenticated, user } = useAuth();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const scrolled = !transparent || trigger;

  const handleMobileNavClick = (href: string, isRouter: boolean) => {
    setMobileOpen(false);
    if (href.startsWith('/#')) {
      const id = href.slice(2);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else if (isRouter) {
      navigate(href);
    } else {
      window.location.href = href;
    }
  };

  return (
    <>
      <StyledAppBar position="sticky" scrolled={scrolled} elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', py: 2 }}>
          {/* Logo */}
          <LogoLink to="/">
            <Logo size="medium" className="logo-icon" />
            <LogoText>Lord of Venues</LogoText>
          </LogoLink>

          {/* Desktop Navigation Links */}
          <NavLinks>
            <RouterLink to="/venues" style={{ textDecoration: 'none' }}>
              <NavLink>Browse Venues</NavLink>
            </RouterLink>
            <Box
              component="a"
              href="/#how-it-works"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              style={{ textDecoration: 'none', cursor: 'pointer' }}
            >
              <NavLink>How It Works</NavLink>
            </Box>
            <RouterLink to="/list-your-venue" style={{ textDecoration: 'none' }}>
              <NavLink>List Your Venue</NavLink>
            </RouterLink>
          </NavLinks>

          {/* Auth Buttons + Mobile Menu Toggle */}
          <AuthButtons>
            <CitySwitcher />
            <ThemeToggle size="medium" />
            {isAuthenticated && user ? (
              <AvatarDropdown user={user} onLogout={() => logout()} isLoggingOut={isLoggingOut} />
            ) : (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
                <RouterLink to="/login" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="text"
                    sx={(t) => ({
                      fontFamily: t.typography.fontFamilyMono,
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 3,
                    })}
                  >
                    Log In
                  </Button>
                </RouterLink>
                <RouterLink to="/register" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="contained"
                    sx={(t) => ({
                      fontFamily: t.typography.fontFamilyMono,
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 3,
                      background: t.palette.gradients?.buttonPrimary,
                      color: t.palette.primary.contrastText,
                      '&:hover': {
                        background: t.palette.gradients?.buttonPrimaryHover,
                      },
                    })}
                  >
                    Sign Up
                  </Button>
                </RouterLink>
              </Box>
            )}
            <MobileMenuButton aria-label="Open navigation menu" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </MobileMenuButton>
          </AuthButtons>
        </Toolbar>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            pt: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, mb: 1 }}>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_ITEMS.map((item) => (
            <ListItem key={item.href} disablePadding>
              <ListItemButton onClick={() => handleMobileNavClick(item.href, item.isRouter)}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontFamily: '"IBM Plex Sans", sans-serif',
                    fontWeight: 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {!isAuthenticated && (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMobileNavClick('/login', true)}>
                  <ListItemText
                    primary="Log In"
                    primaryTypographyProps={{
                      fontFamily: '"IBM Plex Mono", monospace',
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ px: 2, pt: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleMobileNavClick('/register', true)}
                  sx={(t) => ({
                    fontFamily: t.typography.fontFamilyMono,
                    fontWeight: 500,
                    textTransform: 'none',
                    py: 1.5,
                    background: t.palette.gradients?.buttonPrimary,
                    color: t.palette.primary.contrastText,
                    '&:hover': {
                      background: t.palette.gradients?.buttonPrimaryHover,
                    },
                  })}
                >
                  Sign Up
                </Button>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
}
